import {gql, useQuery} from "@apollo/client";
import {endOfMonth, format, parse, parseISO, startOfMonth, subMonths} from "date-fns";
import {useState} from "react";
import {ErrorDisplay, LoadingSpinner} from "../../common";
import {formatDateStr} from "common/util/date";
import {ExportButton} from "common/component/ExportButton";
import {useObservable} from "rxsupport";
import {UtilizationHoursTable} from "./utilization/UtilizationHoursTable";
import {AuthorizationUtilization, ClientInfo, UtilizationPeriod, UtilizedHoursEntry} from "./domain";
import {ExportRequestModal} from "./export/ExportRequestModal";
import {useExportData} from "./export/useExportData";

type Props = {
    clientGuid: string;
    clientInfo?: ClientInfo | null;
};

const UTILIZED_HOURS_QUERY = gql`
    query UtilizedHours($clientProfileGuid: String!) {
        utilizedHours(clientProfileGuid: $clientProfileGuid) {
            authorization {
                guid
                authName
                bhpnAuthNumber
                startDate
                endDate
                status
                serviceLine
                funder
                allServiceType
            }
            periods {
                periodStart
                periodEnd
                utilizedHours {
                    procedureCode
                    practitionerRole
                    authorizedHours
                    directCareHoursRequired
                    utilizedHours
                }
            }
        }
    }
`;

type UtilizedHoursQueryResult = {
    utilizedHours: AuthorizationUtilization;
};

function dashIfBlank(value: string | null | undefined): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : "—";
}

function programLabel(authorization: AuthorizationUtilization["authorization"]): string {
    if (authorization.serviceLine) return authorization.serviceLine;
    const types = authorization.allServiceType ?? [];
    if (types.length > 0) return types.join(", ");
    return "";
}

// Row order and the Level column labels live together, so the sort and the cell can never disagree.
// Salesforce calls the para level Direct Care.
const PRACTITIONER_LEVELS: {role: string; label: string}[] = [
    {role: "DIRECT_CARE", label: "Para"},
    {role: "MID_LEVEL", label: "Mid"},
    {role: "HIGH_LEVEL", label: "High"},
    {role: "GROUP_CARE", label: "Group"},
];

// A row is one practitioner level and one procedure code. The same code is authorized at more than
// one level, and the table shows each level separately until the aggregate view rolls them up.
function rowKey(entry: UtilizedHoursEntry): string {
    return `${entry.practitionerRole ?? ""}|${entry.procedureCode}`;
}

function levelRank(role: string | null): number {
    const index = PRACTITIONER_LEVELS.findIndex((level) => level.role === role);
    return index === -1 ? PRACTITIONER_LEVELS.length : index;
}

const PROCEDURE_CODE_ORDER = ["H2019", "H0032", "H0004", "H2014", "S5110"];

function compareProcedureCode(a: string, b: string): number {
    const rank = (code: string) => {
        const index = PROCEDURE_CODE_ORDER.indexOf(code);
        return index === -1 ? PROCEDURE_CODE_ORDER.length : index;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
}

export function aggregateByProcedureCode(entries: UtilizedHoursEntry[]): UtilizedHoursEntry[] {
    const byCode = new Map<string, UtilizedHoursEntry>();
    for (const entry of entries) {
        const existing = byCode.get(entry.procedureCode);
        if (existing) {
            existing.authorizedHours += entry.authorizedHours ?? 0;
            existing.directCareHoursRequired += entry.directCareHoursRequired ?? 0;
            existing.utilizedHours += entry.utilizedHours ?? 0;
        } else {
            byCode.set(entry.procedureCode, {
                procedureCode: entry.procedureCode,
                practitionerRole: null,
                authorizedHours: entry.authorizedHours ?? 0,
                directCareHoursRequired: entry.directCareHoursRequired ?? 0,
                utilizedHours: entry.utilizedHours ?? 0,
            });
        }
    }
    return Array.from(byCode.values()).sort((a, b) => compareProcedureCode(a.procedureCode, b.procedureCode));
}

const EXPORT_UTILIZATION_HOURS = gql`
    mutation ($filters: String) {
        exportUtilizationHours(filters: $filters) {
            guid
            status
        }
    }
`;

type PeriodViewProps = {
    clientInfo?: ClientInfo | null;
    authorization: AuthorizationUtilization["authorization"];
    period: UtilizationPeriod;
    monthlyPeriods?: UtilizationPeriod[];
    asOf?: Date;
    onExport?: () => void;
    exporting?: boolean;
};

export function PeriodView({clientInfo, authorization, period, monthlyPeriods = [], asOf = new Date(), onExport, exporting = false}: PeriodViewProps) {
    const codeRows = aggregateByProcedureCode(period.utilizedHours ?? []);
    const totalAuthorized = codeRows.reduce((sum, r) => sum + (r.authorizedHours ?? 0), 0);
    const totalRequired = codeRows.reduce((sum, r) => sum + (r.directCareHoursRequired ?? 0), 0);
    const totalUtilized = codeRows.reduce((sum, r) => sum + (r.utilizedHours ?? 0), 0);

    const clientName = clientInfo?.fullName || clientInfo?.name || "";
    const clientId = clientInfo?.bhpnClientId || "";
    const subtitleParts = [
        clientName,
        clientId ? `Client ID ${clientId}` : "",
        authorization.bhpnAuthNumber ? `Auth #${authorization.bhpnAuthNumber}` : "",
    ].filter(Boolean);

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-start justify-between gap-4 pb-4 mb-4 border-b-4 border-red-marron">
                <div>
                    <h2 className="text-2xl font-bold leading-tight text-gray-900">
                        Utilization Hours by Month & Procedure Code
                    </h2>
                    {subtitleParts.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">{subtitleParts.join("  ·  ")}</p>
                    )}
                </div>
                <div className="shrink-0">
                    <ExportButton onClick={onExport} disabled={exporting}/>
                </div>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-2 mb-6 text-sm">
                <Meta label="Authorization Period" value={`${formatDateStr(authorization.startDate)} – ${formatDateStr(authorization.endDate)}`}/>
                <Meta label="Funder" value={dashIfBlank(authorization.funder)}/>
                <Meta label="Service Type" value={dashIfBlank(programLabel(authorization))}/>
                <Meta label="As of" value={formatDateStr(format(asOf, "yyyy-MM-dd"))}/>
            </div>

            <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-2">
                Aggregate — Entire Authorization Period
            </p>

            <UtilizationHoursTable
                firstColumnLabel="Procedure Code"
                ariaLabel="Aggregate utilization hours by procedure code"
                totalRow={{
                    label: "Grand Total — All Procedure Codes",
                    testId: "row-grand-total",
                    authorizedHours: totalAuthorized,
                    directCareHoursRequired: totalRequired,
                    utilizedHours: totalUtilized,
                }}
                rows={codeRows.map((row) => ({
                    procedureCode: row.procedureCode,
                    testId: `row-agg-${row.procedureCode}`,
                    authorizedHours: row.authorizedHours,
                    directCareHoursRequired: row.directCareHoursRequired,
                    utilizedHours: row.utilizedHours,
                }))}
            />

            {monthlyPeriods.length > 0 && (
                <>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-2 mt-6">
                        Details — By Month
                    </p>
                    <UtilizationHoursTable
                        firstColumnLabel="Month / Procedure Code"
                        ariaLabel="Utilization hours by month and procedure code"
                        role="treegrid"
                        groups={monthlyPeriods.map((month) => {
                            const monthRows = aggregateByProcedureCode(month.utilizedHours ?? []);
                            const monthLabel = formatMonthLabel(month.periodStart);
                            return {
                                key: month.periodStart,
                                label: monthLabel,
                                testId: `row-month-${month.periodStart}`,
                                ariaLabel: `${monthLabel}, expand procedure-code detail`,
                                authorizedHours: monthRows.reduce((sum, r) => sum + (r.authorizedHours ?? 0), 0),
                                directCareHoursRequired: monthRows.reduce((sum, r) => sum + (r.directCareHoursRequired ?? 0), 0),
                                utilizedHours: monthRows.reduce((sum, r) => sum + (r.utilizedHours ?? 0), 0),
                                rows: monthRows.map((row) => ({
                                    procedureCode: row.procedureCode,
                                    testId: `row-month-${month.periodStart}-${row.procedureCode}`,
                                    authorizedHours: row.authorizedHours,
                                    directCareHoursRequired: row.directCareHoursRequired,
                                    utilizedHours: row.utilizedHours,
                                })),
                            };
                        })}
                    />
                </>
            )}

            <div className="mt-auto pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-600">
                    Utilization is calculated as Utilized Hours / Minimum Direct Hours Required. The displayed utilization percentage is capped at 100%.
                    <br/>Select a month to expand its procedure-code detail.
                </p>
            </div>
        </div>
    );
}

function Meta({label, value}: {label: string; value: string}) {
    return (
        <div>
            <b className="block text-gray-600 font-semibold uppercase tracking-wide" style={{fontSize: 12}}>{label}</b>
            <span>{value}</span>
        </div>
    );
}

function formatMonthLabel(periodStart: string): string {
    const date = parse(periodStart, "yyyy-MM-dd", new Date());
    return format(date, "MMM yyyy");
}

export function periodsInWindow(
    periods: UtilizationPeriod[],
    today: Date = new Date()
): UtilizationPeriod[] {
    if (periods.length === 0) return [];

    const todayStr = format(today, "yyyy-MM-dd");
    const todayDate = parseISO(todayStr);

    const hasTodayPeriod = periods.some(
        (p) => p.periodStart <= todayStr && p.periodEnd >= todayStr
    );

    const windowStart = format(startOfMonth(subMonths(todayDate, 5)), "yyyy-MM-dd");
    const windowEnd = hasTodayPeriod
        ? format(endOfMonth(todayDate), "yyyy-MM-dd")
        : format(endOfMonth(subMonths(todayDate, 1)), "yyyy-MM-dd");

    return periods
        .filter((p) => p.periodStart <= windowEnd && p.periodEnd >= windowStart)
        .slice()
        .sort((a, b) => b.periodStart.localeCompare(a.periodStart));
}

export function combinePeriods(
    periods: UtilizationPeriod[],
    today: Date = new Date()
): UtilizationPeriod | undefined {
    const filtered = periodsInWindow(periods, today);

    if (filtered.length === 0) return undefined;

    const entriesByRow = new Map<string, UtilizedHoursEntry>();
    let minStart = filtered[0].periodStart;
    let maxEnd = filtered[0].periodEnd;

    for (const period of filtered) {
        if (period.periodStart < minStart) minStart = period.periodStart;
        if (period.periodEnd > maxEnd) maxEnd = period.periodEnd;

        for (const entry of period.utilizedHours ?? []) {
            const key = rowKey(entry);
            const existing = entriesByRow.get(key);
            if (existing) {
                existing.authorizedHours += entry.authorizedHours ?? 0;
                existing.directCareHoursRequired += entry.directCareHoursRequired ?? 0;
                existing.utilizedHours += entry.utilizedHours ?? 0;
            } else {
                entriesByRow.set(key, {
                    procedureCode: entry.procedureCode,
                    practitionerRole: entry.practitionerRole ?? null,
                    authorizedHours: entry.authorizedHours ?? 0,
                    directCareHoursRequired: entry.directCareHoursRequired ?? 0,
                    utilizedHours: entry.utilizedHours ?? 0,
                });
            }
        }
    }

    // Sorting here rather than in the backend, because only this function knows the final set of
    // rows. A code that first appears in a later month would otherwise land at the bottom.
    const sorted = Array.from(entriesByRow.values()).sort(
        (a, b) =>
            levelRank(a.practitionerRole) - levelRank(b.practitionerRole) ||
            compareProcedureCode(a.procedureCode, b.procedureCode)
    );

    return {
        periodStart: minStart,
        periodEnd: maxEnd,
        utilizedHours: sorted,
    };
}

export function ClientProfileUtilizationPage({clientGuid, clientInfo}: Props) {
    const {data, loading, error} = useQuery<UtilizedHoursQueryResult>(UTILIZED_HOURS_QUERY, {
        variables: {clientProfileGuid: clientGuid},
    });
    const {isRunning$: isExporting$, execute: exportData} = useExportData(EXPORT_UTILIZATION_HOURS);
    const isExporting = useObservable(isExporting$, false);
    const [exportGuid, setExportGuid] = useState<string | null>(null);
    const handleExport = async () => {
        const guid = await exportData(JSON.stringify({clientProfileGuid: clientGuid}));
        if (guid) {
            setExportGuid(guid);
        }
    };

    if (loading && !data) {
        return (
            <div className="h-full relative">
                <div className="h-full p-5 bg-white shadow-md scrollbar-always">
                    <LoadingSpinner spacing={20} height={"40vh"}/>
                </div>
            </div>
        );
    }

    if (error) {
        return <ErrorDisplay error={error} title="Error loading utilization data" />;
    }

    const authUtilization: AuthorizationUtilization | undefined = data?.utilizedHours;

    if (!authUtilization) {
        return (
            <div className="h-full relative">
                <div className="h-full p-5 bg-white shadow-md scrollbar-always">
                    <div className="p-4 text-gray-500">No utilization data available.</div>
                </div>
            </div>
        )
    }

    const periods = authUtilization.periods ?? [];
    const monthlyPeriods = periodsInWindow(periods);
    const combinedPeriod = combinePeriods(periods);

    return (
        <div className="h-full relative">
            <div className="h-full p-5 bg-white shadow-md scrollbar-always">
                {combinedPeriod ? (
                    <PeriodView
                        clientInfo={clientInfo}
                        authorization={authUtilization.authorization}
                        period={combinedPeriod}
                        monthlyPeriods={monthlyPeriods}
                        onExport={handleExport}
                        exporting={isExporting}
                    />
                ) : (
                    <div className="p-4 text-gray-500">No utilization periods available.</div>
                )}
            </div>
            <ExportRequestModal guid={exportGuid} onClose={() => setExportGuid(null)}/>
        </div>
    );
}
